import {PermanentTokenForm} from "../../api/Model";
import {inputChange, mapSetState, selectChange, SetState} from "../../util/FormHandlers";
import React, {useMemo} from "react";
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import {RemoteValue, useRemoteValue} from "../../util/RemoteValue";
import {useOfficeStore} from "../../api/OfficeStore";
import {useGlobalKeyVaultStore} from "../../api/GlobalKeyVaultStore";
import {PermissionSelect} from "./PermissionPresentation";
import {stringCmp} from "../../util/SortUtil";

export function PermanentTokenDialog({form, setForm}: {
    form: PermanentTokenForm;
    setForm: SetState<PermanentTokenForm>;
}) {
    const keyVaults = useGlobalKeyVaultStore(state => state.remoteValue);
    const offices = useOfficeStore(state => state.remoteValue);

    const keyVaultTreeRV = useMemo(
        () => RemoteValue
            .combine({keyVaults, offices})
            .map(({keyVaults, offices}) =>
                [...offices]
                    .sort(stringCmp('name'))
                    .map(office => ({
                        office,
                        keyVaults: keyVaults
                            .filter(vault => vault.officeId === office.keyOfficeId)
                            .sort(stringCmp('name')),
                    }))
            ),
        [keyVaults, offices],
    );
    const tree = useRemoteValue(keyVaultTreeRV, []);

    const handlers = useMemo(() => {
        return ({
            handleNameChange: inputChange(setForm, newValue => ({name: newValue})),
            handleDescriptionChange: inputChange(setForm, newValue => ({description: newValue || null})),
            handleOfficeChange: selectChange(setForm, (newOfficeId: number | string) => ({officeId: newOfficeId !== -1 ? Number(newOfficeId) : null})),
            handleVaultChange: selectChange(setForm, (newVaultId: number | string) => ({vaultId: newVaultId !== -1 ? Number(newVaultId) : null})),
        });
    }, [setForm]);

    const setPermissions = useMemo(() => mapSetState(setForm, 'permissions'), [setForm]);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <TextField
                autoFocus
                required
                margin="dense"
                id="name"
                label="Name"
                fullWidth
                variant="outlined"
                value={form.name}
                onChange={handlers.handleNameChange}
            />

            <TextField
                autoFocus
                margin="dense"
                id="description"
                label="Description"
                fullWidth
                variant="outlined"
                multiline
                value={form.description || ''}
                onChange={handlers.handleDescriptionChange}
            />

            <FormControl fullWidth>
                <InputLabel id="office-label">Office</InputLabel>
                <Select<number>
                    labelId="office-label"
                    id="office"
                    value={form.officeId || -1}
                    label="Office"
                    onChange={handlers.handleOfficeChange}
                >
                    <MenuItem value={-1}>Any Office</MenuItem>
                    {tree.map(({office}) => (
                        <MenuItem key={office.id} value={office.id}>{office.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {form.officeId && (
                <FormControl fullWidth>
                    <InputLabel id="vault-label">Vault</InputLabel>
                    <Select
                        labelId="vault-label"
                        id="vault"
                        value={form.vaultId || -1}
                        label="Vault"
                        onChange={handlers.handleVaultChange}
                    >
                        <MenuItem value={-1}>Any Vault</MenuItem>
                        {(tree
                            .find(({office}) => office.id === form.officeId)
                            ?.keyVaults || [])
                            .map((vault) => (
                                <MenuItem key={vault.id} value={vault.id}>{vault.name}</MenuItem>
                            ))}
                    </Select>
                </FormControl>
            )}

            <PermissionSelect
                label="Permissions"
                value={form.permissions || []}
                setValue={setPermissions}
            />
        </Box>
    );
}
